import { useState, useCallback, useRef } from 'react'
import { TDrawingTool } from '../types'
import { eraseImageContent, inpaintImageContent, ImageResult } from '../imageEditingApi'
import { useLoading } from '../editor/loading_state/useLoading'

interface UseInpaintAndEraseOptions {
    imageCanvasRef: React.RefObject<HTMLCanvasElement>
    maskCanvasRef: React.RefObject<HTMLCanvasElement>
    onAccept?: (result: { url: string; width: number; height: number; cleanup: () => void }) => void
    onError?: (error: Error) => void
    onMaskChange?: (blob: Blob) => void
}

interface UseInpaintAndEraseReturn {
    currentTool: TDrawingTool
    brushSize: number
    isProcessing: boolean
    hasMask: boolean
    prompt: string
    showPromptInput: boolean
    showInputAbove: boolean
    setCurrentTool: (tool: TDrawingTool) => void
    setBrushSize: (size: number) => void
    setPrompt: (prompt: string) => void
    startDrawing: (e: React.MouseEvent) => void
    stopDrawing: () => void
    draw: (e: React.MouseEvent) => void
    undo: () => void
    clearMask: () => void
    handleSubmit: () => Promise<void>
}

export const useInpaintAndErase = ({
    imageCanvasRef,
    maskCanvasRef,
    onAccept,
    onError,
    onMaskChange,
}: UseInpaintAndEraseOptions): UseInpaintAndEraseReturn => {
    // UI State
    const [currentTool, setCurrentTool] = useState<TDrawingTool>('brush')
    const [brushSize, setBrushSize] = useState(100)

    // Loading state
    const { startLoading, stopLoading, isLoading } = useLoading('image-editor')

    // Mask State
    const [hasMask, setHasMask] = useState(false)

    // Prompt State
    const [prompt, setPrompt] = useState('')
    const [showPromptInput, setShowPromptInput] = useState(false)
    const [showInputAbove, setShowInputAbove] = useState(false)

    // Drawing State
    const [isDrawing, setIsDrawing] = useState(false)
    const lastPoint = useRef<{ x: number; y: number } | null>(null)

    /**
     * Mask Content Check - checks for any white pixels in the mask
     */
    const checkForMaskContent = useCallback((imageData: ImageData): boolean => {
        const data = imageData.data
        let hasWhitePixels = false
        for (let i = 0; i < data.length; i += 4) {
            // Check for white pixels (255, 255, 255)
            if (data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255) {
                hasWhitePixels = true
                break
            }
        }
        return hasWhitePixels
    }, [])

    /**
     * Mask Change Notification
     */
    const notifyMaskChange = useCallback(() => {
        if (!maskCanvasRef.current || !onMaskChange) return

        maskCanvasRef.current.toBlob((blob) => {
            if (blob) onMaskChange(blob)
        })
    }, [maskCanvasRef, onMaskChange])

    /**
     * Drawing Handlers
     */
    const draw = useCallback(
        (e: React.MouseEvent) => {
            if (!isDrawing || !maskCanvasRef.current) return

            const canvas = maskCanvasRef.current
            const ctx = canvas.getContext('2d')
            if (!ctx) return

            const rect = canvas.getBoundingClientRect()
            // Calculate scale for both dimensions
            const scaleX = canvas.width / rect.width
            const scaleY = canvas.height / rect.height

            // Convert mouse coordinates to canvas coordinates
            const x = (e.clientX - rect.left) * scaleX
            const y = (e.clientY - rect.top) * scaleY

            ctx.strokeStyle = 'white'
            ctx.lineWidth = brushSize
            ctx.lineCap = 'round'

            ctx.beginPath()
            if (lastPoint.current) {
                ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
                ctx.lineTo(x, y)
            } else {
                ctx.moveTo(x, y)
                ctx.lineTo(x, y)
            }
            ctx.stroke()

            lastPoint.current = { x, y }

            // Check if mask has any content
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            const hasContent = checkForMaskContent(imageData)
            setHasMask(hasContent)
            // Only show prompt input for brush tool
            if (currentTool === 'brush') {
                setShowPromptInput(hasContent)
                setShowInputAbove(y > canvas.height / 2)
            }
            notifyMaskChange()
        },
        [isDrawing, brushSize, checkForMaskContent, notifyMaskChange, currentTool, maskCanvasRef],
    )

    const startDrawing = useCallback(
        (e: React.MouseEvent) => {
            setIsDrawing(true)
            lastPoint.current = null
            draw(e)
        },
        [draw],
    )

    const stopDrawing = useCallback(() => {
        if (isDrawing) {
            setIsDrawing(false)
            lastPoint.current = null
        }
    }, [isDrawing])

    /**
     * Clear mask to black background
     */
    const clearMask = useCallback(() => {
        const canvas = maskCanvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setHasMask(false)
        setShowPromptInput(false)
        notifyMaskChange()
    }, [maskCanvasRef, notifyMaskChange])

    /**
     * Tool change handler that clears the mask
     */
    const handleToolChange = useCallback(
        (tool: TDrawingTool) => {
            setCurrentTool(tool)
            clearMask()
        },
        [clearMask],
    )

    /**
     * Undo just clears the mask
     */
    const undo = useCallback(() => {
        clearMask()
    }, [clearMask])

    /**
     * Handle Edit Submission
     */
    const handleSubmit = useCallback(async () => {
        const canvas = maskCanvasRef.current
        const imageCanvas = imageCanvasRef.current
        if (!canvas || !imageCanvas) return

        startLoading(
            currentTool === 'eraser' ? 'Erasing content...' : 'Generating new image...',
            1, // Priority 1 for image processing
        )

        try {
            // Always get data URLs directly from canvases
            const maskBase64 = canvas.toDataURL('image/png')
            const imageBase64 = imageCanvas.toDataURL('image/png')

            // Common parameters with proper data URLs
            const params = {
                imageBase64,
                maskBase64,
                output_format: 'png',
            }

            let result: ImageResult
            if (currentTool === 'eraser') {
                result = await eraseImageContent(params)
            } else {
                // For brush tool, we use inpaint
                result = await inpaintImageContent({
                    ...params,
                    prompt: prompt.trim() || 'Generate content',
                })
            }

            // Update the canvas with the new image and convert to data URL
            const image = new Image()
            await new Promise<void>((resolve) => {
                image.onload = () => {
                    const ctx = imageCanvas.getContext('2d')
                    if (!ctx) {
                        resolve()
                        return
                    }

                    // Update canvas dimensions to match the result image
                    imageCanvas.width = image.width
                    imageCanvas.height = image.height
                    canvas.width = image.width
                    canvas.height = image.height

                    // Clear and draw new image at full size
                    ctx.clearRect(0, 0, image.width, image.height)
                    ctx.drawImage(image, 0, 0, image.width, image.height)

                    // Convert the result to data URL before passing to onAccept
                    if (onAccept) {
                        const dataUrl = imageCanvas.toDataURL('image/png')
                        onAccept({
                            url: dataUrl,
                            width: image.width,
                            height: image.height,
                            cleanup: result.cleanup,
                        })
                    }

                    resolve()
                }
                image.src = result.url
            })

            // Clear the mask
            clearMask()
            // Reset prompt state
            setPrompt('')
        } catch (error) {
            if (onError) {
                onError(error as Error)
            }
        } finally {
            stopLoading()
        }
    }, [
        currentTool,
        prompt,
        onAccept,
        onError,
        clearMask,
        imageCanvasRef,
        maskCanvasRef,
        startLoading,
        stopLoading,
    ])

    return {
        currentTool,
        brushSize,
        isProcessing: isLoading(),
        hasMask,
        prompt,
        showPromptInput,
        showInputAbove,
        setCurrentTool: handleToolChange,
        setBrushSize,
        setPrompt,
        startDrawing,
        stopDrawing,
        draw,
        undo,
        clearMask,
        handleSubmit,
    }
}
